import React, { useMemo } from 'react';
import { Box, useTheme } from '@mui/material';
import { ResponsiveLine } from '@nivo/line';
import { tokens } from '../../theme';
import { useChartTheme } from '../../themes/chartTheme';

export interface ChannelTestDailyPoint {
  date: string;
  bucket: 'A' | 'B';
  qualifying_impressions: number;
  wall_doi: number;
  ad_doi: number;
}

interface Props {
  points: ChannelTestDailyPoint[];
}

const ChannelTestTimelineChart: React.FC<Props> = ({ points }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const chartTheme = useChartTheme();

  const series = useMemo(() => {
    const days = Array.from(new Set(points.map((p) => p.date))).sort();
    const findRow = (date: string, bucket: 'A' | 'B') =>
      points.find((p) => p.date === date && p.bucket === bucket);

    const out = [
      { id: 'A · Wall-DOI', data: [] as { x: string; y: number }[] },
      { id: 'A · Ad-DOI', data: [] as { x: string; y: number }[] },
      { id: 'B · Ad-DOI', data: [] as { x: string; y: number }[] },
    ];

    for (const day of days) {
      const a = findRow(day, 'A');
      const b = findRow(day, 'B');
      out[0].data.push({ x: day, y: a?.wall_doi ?? 0 });
      out[1].data.push({ x: day, y: a?.ad_doi ?? 0 });
      out[2].data.push({ x: day, y: b?.ad_doi ?? 0 });
    }
    return out;
  }, [points]);

  const colorMap: Record<string, string> = {
    'A · Wall-DOI': colors.greenAccent?.[700] ?? '#2e7d32',
    'A · Ad-DOI': colors.greenAccent?.[400] ?? '#66bb6a',
    'B · Ad-DOI': colors.blueAccent?.[500] ?? '#2196f3',
  };

  if (series.every((s) => s.data.length === 0)) {
    return (
      <Box height={380} display="flex" alignItems="center" justifyContent="center">
        Keine Daten für den gewählten Zeitraum.
      </Box>
    );
  }

  return (
    <Box height={380}>
      <ResponsiveLine
        data={series}
        theme={chartTheme}
        margin={{ top: 30, right: 150, bottom: 60, left: 50 }}
        xScale={{ type: 'point' }}
        yScale={{ type: 'linear', min: 0, max: 'auto', stacked: false }}
        axisBottom={{
          tickRotation: -45,
          legend: 'Datum',
          legendOffset: 50,
          legendPosition: 'middle',
        }}
        axisLeft={{ legend: 'DOIs', legendOffset: -40, legendPosition: 'middle' }}
        colors={(serie) => colorMap[serie.id as string] ?? colors.grey?.[500] ?? '#9e9e9e'}
        lineWidth={2}
        pointSize={6}
        useMesh={true}
        enableSlices="x"
        legends={[
          {
            anchor: 'right',
            direction: 'column',
            translateX: 130,
            itemWidth: 110,
            itemHeight: 18,
            symbolSize: 12,
            symbolShape: 'circle',
          },
        ]}
      />
    </Box>
  );
};

export default ChannelTestTimelineChart;
